// Harmonogram sztuki sprzętu dla kalendarza sprzętu (pages/EquipmentCalendar.js).
// Czysty moduł: bez Reacta, bez sieci, „dziś" wstrzykiwane, więc da się go przetestować.
//
// Wejście to surowe wiersze z /api/rentals (rezerwacje) i /api/offer-holds (blokady sprzętu
// z wysłanych ofert). Wyjście to jedna lista WPISÓW, na której stoi cały ekran: wstęgi w siatce,
// zdanie o stanie sztuki, obłożenie miesiąca i agenda z prawej. Jedna lista znaczy jedno
// źródło prawdy: wcześniej każde z tych miejsc liczyło „czy dzień jest zajęty" po swojemu.

import { isCancelledReservation as isCancelled } from './reservationStatus';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Lokalna północ. Daty z API przychodzą jako `RRRR-MM-DD` albo ISO; `new Date('2026-10-02')`
// to północ UTC, czyli w Polsce 1 lub 2 w nocy, więc samo `setHours(0)` wystarcza tylko dla
// ISO. Tekst `RRRR-MM-DD` rozbieramy ręcznie, żeby nie zależeć od strefy.
export function dayStart(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Math.round, bo przy zmianie czasu różnica dwóch lokalnych północy to 23 albo 25 godzin.
export function daysBetweenInclusive(start, end) {
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
}


// Wpis: { kind: 'reservation' | 'offer', id, start, end, name, status, equipmentIds, raw }.
// Anulowane rezerwacje odpadają. Blokady ofert są wierszami PER SZTUKA, więc grupujemy je po
// ofercie: jedna oferta na trzy sztuki to jeden wpis, nie trzy.
export function buildEntries(rentals, offerHolds) {
  const entries = [];
  (rentals || []).forEach((r) => {
    if (!r || isCancelled(r)) return;
    const start = dayStart(r.start_date);
    const end = dayStart(r.end_date);
    if (!start || !end) return;
    const name = [r.firstname, r.lastname].filter(Boolean).join(' ').trim();
    entries.push({
      kind: 'reservation',
      id: r.id,
      start,
      end,
      name,
      status: r.reservation_status || '',
      equipmentIds: Array.isArray(r.equipment) ? r.equipment : [],
      raw: r,
    });
  });

  const offers = new Map();
  (offerHolds || []).forEach((h) => {
    const offerId = h?.sent_offer_id;
    if (offerId == null) return;
    const start = dayStart(h.date_from);
    const end = dayStart(h.date_to);
    if (!start || !end) return;
    const existing = offers.get(offerId);
    if (existing) {
      if (h.equipment_id != null && !existing.equipmentIds.includes(h.equipment_id)) existing.equipmentIds.push(h.equipment_id);
      return;
    }
    offers.set(offerId, {
      kind: 'offer',
      id: offerId,
      start,
      end,
      name: h.client_name || h.email || '',
      status: '',
      equipmentIds: h.equipment_id != null ? [h.equipment_id] : [],
      raw: h,
    });
  });
  entries.push(...offers.values());

  // Rezerwacje przed ofertami tego samego dnia: rezerwacja to fakt, oferta to zamiar.
  return entries.sort((a, b) => (a.start - b.start) || (a.kind === b.kind ? 0 : a.kind === 'reservation' ? -1 : 1) || (a.end - b.end));
}

export function entryCovers(entry, date) {
  const d = dayStart(date);
  return !!d && d >= entry.start && d <= entry.end;
}

export function entriesOnDay(entries, date) {
  return (entries || []).filter((e) => entryCovers(e, date));
}

// Kawałek wstęgi w komórce dnia. Etykieta (nazwisko) stoi na początku wstęgi i na początku
// każdego wiersza siatki (poniedziałek), żeby wstęga zaczęta w poprzednim tygodniu też była
// podpisana — inaczej trzeba by szukać jej początku wiersz wyżej.
export function ribbonPiece(entry, date) {
  const d = dayStart(date);
  const isStart = d.getTime() === entry.start.getTime();
  const isEnd = d.getTime() === entry.end.getTime();
  const weekStart = d.getDay() === 1;
  const weekEnd = d.getDay() === 0;
  // Ile dni od tego dnia wstęga jeszcze biegnie w TYM wierszu siatki (do końca wpisu albo do
  // niedzieli). Etykieta może być tak szeroka, żeby nazwisko nie urywało się na pierwszym dniu.
  const doNiedzieli = (7 - d.getDay()) % 7;
  const koniecWiersza = new Date(d.getFullYear(), d.getMonth(), d.getDate() + doNiedzieli);
  const koniec = entry.end < koniecWiersza ? entry.end : koniecWiersza;
  const spanDays = daysBetweenInclusive(d, koniec);
  // Jednodniowy kawałek na końcu tygodnia (rezerwacja zaczyna się w niedzielę) nie mieści
  // nazwiska; skoro wstęga biegnie dalej, podpis dostaje dopiero poniedziałek. Wpis, który
  // kończy się w tym wierszu, zostaje podpisany zawsze, bo innego miejsca na podpis nie ma.
  const podpisNaPoczatku = isStart && (spanDays >= 2 || koniec.getTime() === entry.end.getTime());
  return {
    isStart,
    isEnd,
    // zaokrąglenie z lewej/prawej: początek/koniec wstęgi albo krawędź wiersza tygodnia
    roundLeft: isStart || weekStart,
    roundRight: isEnd || weekEnd,
    showLabel: podpisNaPoczatku || weekStart,
    spanDays,
  };
}

export const entryKey = (entry) => `${entry.kind}:${entry.id}`;

// Stały tor każdego wpisu: wstęga nie skacze między wierszami, gdy w jednym dniu spotkają się
// dwie rezerwacje (zwrot rano, wydanie po południu) albo rezerwacja i oferta. Zachłannie,
// chronologicznie; tor jest wolny, gdy jego ostatni wpis skończył się PRZED początkiem bieżącego.
export function assignLanes(entries) {
  const laneEnds = [];
  const lanes = new Map();
  (entries || []).forEach((e) => {
    let lane = laneEnds.findIndex((end) => end < e.start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(e.end); } else { laneEnds[lane] = e.end; }
    lanes.set(entryKey(e), lane);
  });
  return lanes;
}

// Stan sztuki dziś: wypożyczona (do kiedy i komu), zablokowana ofertą albo wolna (i kiedy
// najbliższa rezerwacja). To zdanie stoi na górze ekranu i odpowiada na pytanie, z którym
// obsługa najczęściej tu wchodzi: „mogę to komuś dać?". `wymiana` to flaga firmy (Zasady wynajmu),
// `equipmentId` to ta sztuka: w dniu wymiany po zapisanym zwrocie jest już u następnego klienta.
export function itemStatus(entries, today, { wymiana = false, equipmentId } = {}) {
  const d = dayStart(today);
  const now = entriesOnDay(entries, d);
  const rezerwacjeDzis = now.filter((e) => e.kind === 'reservation');
  const zwrocona = (e) => (e.raw?.returned_equipment || []).map(Number).includes(Number(equipmentId));
  // Przy wymianie sztuka jest u klienta pierwszej rezerwacji bez zapisanego zwrotu, a gdy oddali już
  // wszyscy, zdanie mówi o ostatnim z nich: data końca i nazwisko muszą dotyczyć tej samej rezerwacji.
  const reservationNow = wymiana
    ? (rezerwacjeDzis.find((e) => !zwrocona(e)) || rezerwacjeDzis[rezerwacjeDzis.length - 1])
    : rezerwacjeDzis[0];
  if (reservationNow) {
    // kilka rezerwacji jedna po drugiej bez przerwy = zajęta do końca ostatniej. Następna zaczyna
    // się dzień po poprzedniej, a przy włączonej wymianie (N.1) także w dniu jej zwrotu. Bez tej
    // flagi styk nie przedłuża łańcucha: rezerwacje stykają się wtedy tylko po wcześniejszym
    // zwrocie albo przy konflikcie z opłaconej oferty, a zdanie ma zostać takie jak dotąd.
    let until = reservationNow.end;
    let guard = 0;
    for (;;) {
      // dzień po `until` z daty, nie z +24 h: przy zmianie czasu doba ma 23 albo 25 godzin
      const dzienPo = new Date(until.getFullYear(), until.getMonth(), until.getDate() + 1).getTime();
      const dzienKonca = until.getTime();
      const next = entries.find((e) => e.kind === 'reservation' && (e.start.getTime() === dzienPo
        || (wymiana && e.end > until && e.start.getTime() === dzienKonca)));
      if (!next || guard++ > 50) break;
      until = next.end;
    }
    return { state: 'rented', entry: reservationNow, until };
  }
  const offerNow = now.find((e) => e.kind === 'offer');
  const next = (entries || []).find((e) => e.kind === 'reservation' && e.start > d) || null;
  if (offerNow) return { state: 'held', entry: offerNow, until: offerNow.end, next };
  return { state: 'free', next };
}

// Ile dni miesiąca sztuka jest zajęta rezerwacjami (oferty się nie liczą — to jeszcze nie
// wypożyczenie). Dzień z dwiema rezerwacjami liczy się raz.
export function monthOccupancy(entries, monthDate) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const totalDays = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const reservations = (entries || []).filter((e) => e.kind === 'reservation');
  let busyDays = 0;
  for (let i = 0; i < totalDays; i += 1) {
    const day = new Date(first.getFullYear(), first.getMonth(), i + 1);
    if (reservations.some((e) => day >= e.start && day <= e.end)) busyDays += 1;
  }
  return { busyDays, totalDays };
}

// Wpisy nachodzące na okno [od, od + dni), chronologicznie. Agenda z prawej strony.
export function agenda(entries, from, days = 30) {
  const start = dayStart(from);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + days - 1);
  return (entries || []).filter((e) => e.end >= start && e.start <= end);
}
